/**
 * Elite Fade Designs - Install PWA Button
 *
 * Renders an "Install App" button that uses the browser's native
 * beforeinstallprompt event (Chrome, Edge, Android). On iOS Safari
 * (which doesn't fire the event), shows a small modal with manual
 * "Add to Home Screen" instructions.
 *
 * Behavior (Option A):
 * - Hidden if the app is already installed (display-mode: standalone)
 * - Hidden if the browser can't install AND it's not iOS
 * - Visible otherwise; disappears after successful install
 */

let deferredPrompt = null;
let buttonRef = null;

export function InstallPWAButton() {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'footer-install-btn';
  btn.hidden = true;
  btn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
    <span>Install App</span>
  `;

  buttonRef = btn;

  if (isStandalone()) {
    return btn;
  }

  if (isIOS()) {
    btn.hidden = false;
    btn.addEventListener('click', showIOSInstructions);
    return btn;
  }

  if (deferredPrompt) {
    btn.hidden = false;
  }

  btn.addEventListener('click', async () => {
    if (!deferredPrompt) return;
    btn.disabled = true;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (outcome === 'accepted') {
      btn.hidden = true;
    } else {
      btn.disabled = false;
    }
  });

  return btn;
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  if (buttonRef && !isStandalone()) {
    buttonRef.hidden = false;
  }
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  if (buttonRef) buttonRef.hidden = true;
});

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || '';
  const isIosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIosDevice && isSafari;
}

function showIOSInstructions() {
  if (document.querySelector('.ios-install-overlay')) return;

  const overlay = document.createElement('div');
  overlay.className = 'ios-install-overlay';
  overlay.innerHTML = `
    <div class="ios-install-backdrop"></div>
    <div class="ios-install-modal" role="dialog" aria-label="Install app">
      <button type="button" class="ios-install-close" aria-label="Close">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
      <h3 class="ios-install-title">Install Elite Fade</h3>
      <p class="ios-install-text">Add this app to your home screen for the best experience.</p>
      <ol class="ios-install-steps">
        <li>
          Tap the
          <svg class="ios-install-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          Share button at the bottom of Safari.
        </li>
        <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong> in the top right.</li>
      </ol>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  const close = () => {
    overlay.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', escListener);
  };
  const escListener = (e) => { if (e.key === 'Escape') close(); };

  overlay.querySelector('.ios-install-backdrop').addEventListener('click', close);
  overlay.querySelector('.ios-install-close').addEventListener('click', close);
  document.addEventListener('keydown', escListener);
}
